import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOwnerView } from '../context/OwnerViewContext';

// No-access screen shown when a user navigates to a route they lack permission for
const NoAccess = () => (
  <div style={{
    minHeight: '100vh',
    background: '#0a0f1e',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '16px',
    color: '#94a3b8',
    fontFamily: 'inherit'
  }}>
    <div style={{ fontSize: '3rem' }}>🔒</div>
    <h2 style={{ color: '#f1f5f9', margin: 0, fontSize: '1.5rem' }}>Access Denied</h2>
    <p style={{ margin: 0, textAlign: 'center', maxWidth: '380px', lineHeight: 1.6 }}>
      You do not have permission to view this page. Contact your administrator to request access.
    </p>
    <a href="/" style={{
      marginTop: '8px',
      padding: '10px 24px',
      background: '#3b82f6',
      color: '#fff',
      borderRadius: '8px',
      textDecoration: 'none',
      fontWeight: 600,
      fontSize: '0.9rem'
    }}>
      Go to Dashboard
    </a>
  </div>
);

/**
 * PrivateRoute
 * - Redirects unauthenticated users to /login
 * - If `permission` prop is provided, shows NoAccess screen for users lacking that permission
 *   (admins and owners always bypass permission checks)
 * - Owner in view mode always has full access to all routes
 */
const PrivateRoute = ({ children, permission }) => {
  const { user, loading } = useAuth();
  const { viewMode } = useOwnerView();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0a0f1e' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;

  // Owner in view mode always has full access — they're testing the app
  if (viewMode) return children;

  // Admins and owners always have full access
  if (permission && user.role !== 'admin' && user.role !== 'owner') {
    if (!user.permissions?.[permission]) {
      return <NoAccess />;
    }
  }

  return children;
};

export default PrivateRoute;
